import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { 
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Mock user data
const mockUsers = [
  { id: 1, firstName: 'John', lastName: 'Doe', email: 'john.doe@example.com', role: 'admin', isActive: true },
  { id: 2, firstName: 'Jane', lastName: 'Smith', email: 'jane.smith@example.com', role: 'tax_manager', isActive: true },
  { id: 3, firstName: 'Bob', lastName: 'Johnson', email: 'bob.johnson@example.com', role: 'viewer', isActive: true },
];

// Mock integration data
const mockIntegrations = [
  { id: 1, name: 'Global Tax Rates API', type: 'tax_rates', status: 'active', lastSync: '2024-04-25T10:30:00' },
  { id: 2, name: 'Currency Exchange Service', type: 'exchange_rates', status: 'active', lastSync: '2024-04-27T14:15:00' },
  { id: 3, name: 'ERP Data Connector', type: 'data_import', status: 'inactive', lastSync: null },
];

function Settings() {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState(mockUsers);
  const [integrations, setIntegrations] = useState(mockIntegrations);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'viewer',
    password: '',
    confirmPassword: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setEditMode(false);
    setCurrentUserId(null);
    setSuccessMessage('');
  };

  // Handle input changes for new/edit user
  const handleUserInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value
    });
  };

  // Handle adding a new user
  const handleAddUser = () => {
    if (editMode) {
      // Update existing user
      setUsers(users.map(user => 
        user.id === currentUserId ? { ...user, ...newUser } : user
      ));
      setSuccessMessage('User updated successfully');
    } else {
      // Add new user
      const user = {
        id: users.length + 1,
        ...newUser,
        isActive: true
      };
      setUsers([...users, user]);
      setSuccessMessage('User added successfully');
    }
    
    // Reset form
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      role: 'viewer',
      password: '',
      confirmPassword: ''
    });
    setEditMode(false);
    setCurrentUserId(null);
  };

  // Handle editing a user
  const handleEditUser = (user) => {
    setNewUser({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      password: '',
      confirmPassword: ''
    });
    setEditMode(true);
    setCurrentUserId(user.id);
    setSuccessMessage('');
  };

  // Handle toggling user active status
  const handleToggleUserStatus = (userId) => {
    setUsers(users.map(user => 
      user.id === userId ? { ...user, isActive: !user.isActive } : user
    ));
  };

  // Handle integration sync
  const handleSyncIntegration = (integrationId) => {
    setIntegrations(integrations.map(integration => 
      integration.id === integrationId 
        ? { ...integration, lastSync: new Date().toISOString() } 
        : integration
    ));
    setSuccessMessage('Integration synced successfully');
  };

  // Handle toggling integration status
  const handleToggleIntegrationStatus = (integrationId) => {
    setIntegrations(integrations.map(integration => 
      integration.id === integrationId 
        ? { ...integration, status: integration.status === 'active' ? 'inactive' : 'active' } 
        : integration
    ));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Render user management tab
  const renderUserManagement = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {editMode ? 'Edit User' : 'Add New User'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  name="firstName"
                  autoComplete="off"
                  value={newUser.firstName}
                  onChange={handleUserInputChange}
                  margin="normal"
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  name="lastName"
                  autoComplete="off"
                  value={newUser.lastName}
                  onChange={handleUserInputChange}
                  margin="normal"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  fullWidth
                  name="email"
                  autoComplete="off"
                  type="email"
                  value={newUser.email}
                  onChange={handleUserInputChange}
                  margin="normal"
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={newUser.role}
                    label="Role"
                    onChange={handleUserInputChange}
                  >
                    <MenuItem value="admin">Administrator</MenuItem>
                    <MenuItem value="tax_manager">Tax Manager</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  fullWidth
                  name="password"
                  autoComplete="off"
                  type={passwordVisible ? "text" : "password"}
                  value={newUser.password}
                  onChange={handleUserInputChange}
                  margin="normal"
                  required={!editMode}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  fullWidth
                  name="confirmPassword"
                  autoComplete="off"
                  type={passwordVisible ? "text" : "password"}
                  value={newUser.confirmPassword}
                  onChange={handleUserInputChange}
                  margin="normal"
                  required={!editMode}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={passwordVisible} 
                      onChange={() => setPasswordVisible(!passwordVisible)} 
                    />
                  }
                  label="Show Password"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  {editMode && (
                    <Button 
                      variant="outlined" 
                      onClick={() => {
                        setEditMode(false);
                        setCurrentUserId(null);
                        setNewUser({
                          firstName: '',
                          lastName: '',
                          email: '',
                          role: 'viewer',
                          password: '',
                          confirmPassword: ''
                        });
                      }}
                      sx={{ mr: 1 }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    startIcon={editMode ? <SaveIcon /> : <AddIcon />}
                    onClick={handleAddUser}
                    disabled={
                      !newUser.firstName || 
                      !newUser.lastName || 
                      !newUser.email || 
                      (!editMode && (!newUser.password || !newUser.confirmPassword)) ||
                      (!editMode && newUser.password !== newUser.confirmPassword)
                    }
                  >
                    {editMode ? 'Save Changes' : 'Add User'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Management
            </Typography>
            
            <List>
              {users.map((user) => (
                <React.Fragment key={user.id}>
                  <ListItem>
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {user.email}
                          </Typography>
                          {` — ${user.role === 'admin' ? 'Administrator' : user.role === 'tax_manager' ? 'Tax Manager' : 'Viewer'}`}
                          {!user.isActive && ' (Inactive)'}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleEditUser(user)} sx={{ mr: 1 }}>
                        <EditIcon />
                      </IconButton>
                      <Switch
                        edge="end"
                        checked={user.isActive}
                        onChange={() => handleToggleUserStatus(user.id)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Render integrations tab
  const renderIntegrations = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              External Integrations
            </Typography>
            
            <List>
              {integrations.map((integration) => (
                <React.Fragment key={integration.id}>
                  <ListItem>
                    <ListItemText
                      primary={integration.name}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {integration.type === 'tax_rates' 
                              ? 'Tax Rates Provider' 
                              : integration.type === 'exchange_rates'
                                ? 'Exchange Rates Provider'
                                : 'Data Import Connector'}
                          </Typography>
                          {` — Last synced: ${formatDate(integration.lastSync)}`}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleSyncIntegration(integration.id)} 
                        disabled={integration.status !== 'active'}
                        sx={{ mr: 1 }}
                      >
                        <RefreshIcon />
                      </IconButton>
                      <Switch
                        edge="end"
                        checked={integration.status === 'active'}
                        onChange={() => handleToggleIntegrationStatus(integration.id)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
              >
                Add New Integration
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Render system settings tab
  const renderSystemSettings = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              General Settings
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Default Currency</InputLabel>
                  <Select
                    value="USD"
                    label="Default Currency"
                  >
                    <MenuItem value="USD">US Dollar (USD)</MenuItem>
                    <MenuItem value="EUR">Euro (EUR)</MenuItem>
                    <MenuItem value="GBP">British Pound (GBP)</MenuItem>
                    <MenuItem value="JPY">Japanese Yen (JPY)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Default Tax Period</InputLabel>
                  <Select
                    value="quarterly"
                    label="Default Tax Period"
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Enable Multi-Currency Support"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Enable Audit Logging"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<SaveIcon />}
                  >
                    Save Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Data Management
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Auto-Lock Periods After</InputLabel>
                  <Select
                    value="90"
                    label="Auto-Lock Periods After"
                  >
                    <MenuItem value="30">30 Days</MenuItem>
                    <MenuItem value="60">60 Days</MenuItem>
                    <MenuItem value="90">90 Days</MenuItem>
                    <MenuItem value="never">Never Auto-Lock</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Data Retention Period</InputLabel>
                  <Select
                    value="7"
                    label="Data Retention Period"
                  >
                    <MenuItem value="1">1 Year</MenuItem>
                    <MenuItem value="3">3 Years</MenuItem>
                    <MenuItem value="5">5 Years</MenuItem>
                    <MenuItem value="7">7 Years</MenuItem>
                    <MenuItem value="10">10 Years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch defaultChecked />}
                  label="Enable Version History"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ mr: 1 }}
                  >
                    Clear Cache
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<SaveIcon />}
                  >
                    Save Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="User Management" />
        <Tab label="Integrations" />
        <Tab label="System Settings" />
      </Tabs>
      
      {tabValue === 0 && renderUserManagement()}
      {tabValue === 1 && renderIntegrations()}
      {tabValue === 2 && renderSystemSettings()}
    </Box>
  );
}

export default Settings;
